import { parseNoteName } from "./notes";
import { createEmptyClip, type ClipIR, type PatternTrack } from "./types";

/** One harmonic cell of the progression: bass pair, triad, and its 16-step motif. */
type HarmonicCell = {
  root: string;
  fifth: string;
  /** Triad ordered low → high. */
  chord: readonly [string, string, string];
  /** 16 sixteenths; `null` is a rest. */
  motif: readonly (string | null)[];
};

const CHORD_CYCLE: readonly HarmonicCell[] = [
  {
    root: "E2",
    fifth: "B1",
    chord: ["E4", "G4", "B4"],
    motif: [
      "E5", "G5", "B5", "E6", "D6", "B5", "G5", "B5",
      "E5", null, "G5", "B5", "A5", "G5", "F#5", "E5",
    ],
  },
  {
    root: "C2",
    fifth: "G1",
    chord: ["C4", "E4", "G4"],
    motif: [
      "G5", "C6", "B5", "G5", "E5", null, "G5", "B5",
      "C6", "B5", "G5", "E5", "D5", "E5", "G5", "A5",
    ],
  },
  {
    root: "D2",
    fifth: "A1",
    chord: ["D4", "F#4", "A4"],
    motif: [
      "A5", "D6", "C6", "A5", "F#5", "A5", "D6", null,
      "E6", "D6", "A5", "F#5", "G5", "A5", "C6", "D6",
    ],
  },
  {
    root: "B1",
    fifth: "F#1",
    chord: ["B3", "D4", "F#4"],
    motif: [
      "B5", "D6", "F#6", "E6", "D6", "B5", "A5", "F#5",
      "B5", null, "D6", "F#6", "A6", "F#6", "D6", "B5",
    ],
  },
];

/** Rotates the progression per 8-bar section so the loop keeps turning over. */
const SECTION_OFFSETS = [0, 0, 1, 2, 3, 2, 1, 0] as const;

const BAR_COUNT = 64;
const STEPS_PER_BAR = 16;
export const ODINIS_TOTAL_STEPS = BAR_COUNT * STEPS_PER_BAR;

/**
 * Cut gates in sixteenths of a step (`Cxx` value / 16 = gate length in steps).
 * Whole-bar chords use ~0xF0 (15/16 bar); drums stay short.
 */
const GATE = {
  chordWhole: 0xf0,
  chordHalf: 0x80,
  bassQuarter: 0x40,
  bassEighth: 0x20,
  bassPickup: 0x10,
  kick: 0x05,
  snare: 0x03,
  hat: 0x01,
  hatAccent: 0x02,
  melodySixteenth: 0x03,
  melodyEighth: 0x06,
} as const;

/** The kernel has no noise-only voices, so drums are pitched one-shots. */
const KICK_NOTE = "E1";
const SNARE_NOTE = "D3";
const HAT_NOTE = "F4";

const TRACK_NAMES = [
  "Melody",
  "Bass",
  "Chord 1",
  "Chord 2",
  "Chord 3",
  "Kick",
  "Snare",
  "Hat",
] as const;

function put(
  track: PatternTrack,
  step: number,
  note: string,
  velocity: number,
  gate: number,
): void {
  const midi = parseNoteName(note);
  if (midi === undefined) {
    throw new Error(`Odinis demo: unparsable note "${note}"`);
  }
  track.cells[step] = {
    note: midi,
    volume: Math.min(1, Math.max(0, velocity)),
    fx: [{ kind: "cut", value: gate }],
  };
}

/**
 * 64-bar / 1024-step port of the Odinis title theme (128 BPM, 16th grid).
 *
 * Eight sections of eight bars walk `SECTION_OFFSETS` through `CHORD_CYCLE`:
 * bars 0–3 are a quiet drumless intro, section 5 is a half-time break, and
 * sections 6–7 are a double-time push. The mono chord voice is spread across
 * three tracks so the triad can sound together.
 */
export function createOdinisDemoClip(): ClipIR {
  const clip = createEmptyClip({ bpm: 128, steps: ODINIS_TOTAL_STEPS, stepDivision: 16 });
  const tracks = clip.patterns[0]!.tracks;
  for (let i = 0; i < tracks.length; i++) {
    tracks[i]!.name = TRACK_NAMES[i] ?? tracks[i]!.name;
  }
  const [melody, bass, chordHigh, chordMid, chordLow, kick, snare, hat] = tracks as [
    PatternTrack, PatternTrack, PatternTrack, PatternTrack,
    PatternTrack, PatternTrack, PatternTrack, PatternTrack,
  ];

  for (let bar = 0; bar < BAR_COUNT; bar++) {
    const barStep = bar * STEPS_PER_BAR;
    const section = Math.floor(bar / 8);
    const harmony = CHORD_CYCLE[
      (Math.floor(bar / 2) + SECTION_OFFSETS[section]!) % CHORD_CYCLE.length
    ]!;
    const quiet = section === 0 && bar < 4;
    const breakBar = section === 5;
    const finalPush = section >= 6;
    const motif = harmony.motif;

    const chordGate = breakBar ? GATE.chordHalf : GATE.chordWhole;
    const chordVelocity = breakBar ? 0.16 : 0.24;
    put(chordLow, barStep, harmony.chord[0], chordVelocity, chordGate);
    put(chordMid, barStep, harmony.chord[1], chordVelocity, chordGate);
    put(chordHigh, barStep, harmony.chord[2], chordVelocity, chordGate);

    for (let beat = 0; beat < 4; beat++) {
      const beatStep = barStep + beat * 4;
      put(
        bass,
        beatStep,
        beat % 2 === 0 ? harmony.root : harmony.fifth,
        quiet ? 0.34 : 0.58,
        finalPush ? GATE.bassEighth : GATE.bassQuarter,
      );
      if (finalPush || beat === 3) {
        put(bass, beatStep + 2, harmony.root, 0.38, GATE.bassPickup);
      }

      if (!quiet) {
        put(kick, beatStep, KICK_NOTE, breakBar ? 0.28 : 0.55, GATE.kick);
        if (beat % 2 === 1) {
          put(snare, beatStep, SNARE_NOTE, breakBar ? 0.14 : 0.24, GATE.snare);
        }
      }
    }

    for (let step = 0; step < STEPS_PER_BAR; step++) {
      const cellStep = barStep + step;

      if (!quiet && (finalPush || step % 2 === 0 || (section >= 3 && step % 4 === 3))) {
        const accent = finalPush && step % 4 === 2;
        put(hat, cellStep, HAT_NOTE, accent ? 0.32 : 0.2, accent ? GATE.hatAccent : GATE.hat);
      }

      const note = motif[(step + (bar % 2) * 3 + section) % motif.length];
      const sparse = section === 0 && step % 2 === 1;
      const dropped = breakBar && step % 4 !== 0 && bar % 2 === 0;
      if (note && !sparse && !dropped) {
        put(
          melody,
          cellStep,
          note,
          0.36 + Math.min(section, 5) * 0.035,
          finalPush && step % 4 === 3 ? GATE.melodyEighth : GATE.melodySixteenth,
        );
      }
    }
  }

  return clip;
}
